/**
 * SizilPro — useSignatureTracking
 * Manages proposal sending and provider-agnostic signing status tracking.
 *
 * Important:
 * - The database still uses legacy Dropbox-named columns during the DocuSeal refactor.
 * - This hook intentionally hides that from the UI layer.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { SignatureRequest } from "../types";

type SendProposalResponse = {
  success: boolean;
  provider?: "docuseal" | "simple" | string;
  signing_url?: string;
  pdf_url?: string;
  provider_request_id?: string;
  provider_submitter_id?: string | null;
  email_sent?: boolean;
  email_skipped_reason?: string | null;
  error?: string;
};

const signatureRequestKey = (proposalId: string | undefined) =>
  ["signature-request", proposalId ?? "missing"] as const;

const proposalSigningUrlKey = (proposalId: string | undefined) =>
  ["proposal-signing-url", proposalId ?? "missing"] as const;

async function getLatestSignatureRequest(
  proposalId: string
): Promise<SignatureRequest | null> {
  const { data, error } = await supabase
    .from("signature_requests")
    .select("*")
    .eq("proposal_id", proposalId)
    .order("sent_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// ─── Fetch latest signature request for a proposal ────────────
export function useSignatureRequest(proposalId: string | undefined) {
  return useQuery<SignatureRequest | null>({
    queryKey: signatureRequestKey(proposalId),
    queryFn: async () => {
      if (!proposalId) return null;
      return getLatestSignatureRequest(proposalId);
    },
    enabled: !!proposalId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" || status === "sent" || status === "viewed"
        ? 30_000
        : false;
    },
  });
}

// ─── Send proposal (triggers Edge Function) ───────────────────
export function useSendProposal() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      proposalId,
      message,
    }: {
      proposalId: string;
      message?: string;
    }) => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke<SendProposalResponse>(
        "send-proposal",
        {
          body: { proposalId, message },
          headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
        }
      );

      if (error) {
        // Extract real error body from edge-function non-2xx response.
        let message = error.message || "Send failed";
        try {
          const ctx = (error as unknown as { context?: Response }).context;
          if (ctx) {
            const body = await ctx.clone().json() as { error?: string; message?: string };
            if (body?.error)   message = body.error;
            else if (body?.message) message = body.message;
          }
        } catch {
          // ignore
        }
        throw new Error(message);
      }
      if (!data?.success) throw new Error(data?.error ?? "Send failed");

      return data;
    },
    onSuccess: async (data, { proposalId }) => {
      qc.invalidateQueries({ queryKey: ["proposals", "detail", proposalId] });
      qc.invalidateQueries({ queryKey: signatureRequestKey(proposalId) });
      qc.invalidateQueries({ queryKey: proposalSigningUrlKey(proposalId) });
      qc.invalidateQueries({ queryKey: ["proposals"] });

      if (data?.signing_url) {
        qc.setQueryData(proposalSigningUrlKey(proposalId), data.signing_url);
      }

      // Prime signature request cache right away so the detail screen updates faster
      try {
        const latest = await getLatestSignatureRequest(proposalId);
        qc.setQueryData(signatureRequestKey(proposalId), latest);
      } catch {
        // ignore cache priming failures; invalidation already happened
      }
    },
  });
}

// ─── Get provider signing URL (for preview in-app) ────────────
export function useProposalSigningUrl(proposalId: string | undefined) {
  return useQuery<string | null>({
    queryKey: proposalSigningUrlKey(proposalId),
    queryFn: async () => {
      if (!proposalId) return null;

      const { data, error } = await supabase
        .from("proposals")
        .select("dropbox_signing_url,status")
        .eq("id", proposalId)
        .single();

      if (error) throw error;

      // Legacy column name still stores the active DocuSeal signing URL
      return data?.dropbox_signing_url ?? null;
    },
    enabled: !!proposalId,
    refetchInterval: 30_000,
  });
}

// ─── Backward-compatible alias during refactor ────────────────
export const useEmbeddedSigningUrl = useProposalSigningUrl;